import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { EmailService } from '../../services/EmailService';
import { DemoAccessService } from '../../services/DemoAccessService';
import { PersonalizedDemoCredentials } from '../../config/demoCredentials';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.xl};
  max-width: 500px;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  background: #1a365d;
  color: white;
  padding: ${Spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const ModalBody = styled.div`
  padding: ${Spacing.xl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: ${BorderRadius.md};
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #1a365d;
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: #a0c93f;
  color: white;
  padding: 1rem;
  border-radius: ${BorderRadius.md};
  font-weight: 700;
  font-size: 1.1rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: ${Spacing.md};
  transition: background-color 0.2s, transform 0.2s;

  &:hover:not(:disabled) {
    background-color: #8db237;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessState = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.lg};
  padding: ${Spacing.xl} 0;
`;

const ErrorText = styled.p`
  color: #e53e3e;
  font-size: 0.875rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DemoRequestModal: React.FC<DemoRequestModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issuedCredentials, setIssuedCredentials] = useState<PersonalizedDemoCredentials | null>(null);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const loginUrl = `${window.location.origin}/login`;
      const credentials = await DemoAccessService.provision(formData);

      const result = await EmailService.sendDemoCredentials(
        formData.email,
        formData.name,
        loginUrl,
        credentials
      );

      await EmailService.sendDemoRequestNotification({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        managerUsername: credentials.manager.username,
        salesRepUsername: credentials.salesRep.username
      });

      if (!result.success) {
        console.warn('Demo credential email failed:', result.error);
      }

      setIssuedCredentials(credentials);
      setIsSuccess(true);
    } catch (err: any) {
      setError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Request Platform Demo</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {isSuccess ? (
            <SuccessState>
              <CheckCircle2 size={64} color="#48bb78" />
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#1a365d' }}>Request Received!</h4>
                <p style={{ color: '#4a5568', margin: 0 }}>
                  We emailed <strong>manager</strong> and <strong>sales rep</strong> logins to <strong>{formData.email}</strong>. You can also use them right now:
                </p>
              </div>
              <div style={{ width: '100%', textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '1rem' }}>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: 700, color: '#1a365d' }}>1. {issuedCredentials?.manager.label}</p>
                <p style={{ margin: '0 0 0.25rem 0', color: '#4a5568' }}><strong>Username:</strong> {issuedCredentials?.manager.username}</p>
                <p style={{ margin: '0 0 0.25rem 0', color: '#4a5568' }}><strong>Email:</strong> {issuedCredentials?.manager.email}</p>
                <p style={{ margin: '0 0 1rem 0', color: '#4a5568' }}><strong>Password:</strong> {issuedCredentials?.manager.password}</p>
                <p style={{ margin: '0 0 0.75rem 0', fontWeight: 700, color: '#1a365d' }}>2. {issuedCredentials?.salesRep.label}</p>
                <p style={{ margin: '0 0 0.25rem 0', color: '#4a5568' }}><strong>Username:</strong> {issuedCredentials?.salesRep.username}</p>
                <p style={{ margin: '0 0 0.25rem 0', color: '#4a5568' }}><strong>Email:</strong> {issuedCredentials?.salesRep.email}</p>
                <p style={{ margin: 0, color: '#4a5568' }}><strong>Password:</strong> {issuedCredentials?.salesRep.password}</p>
              </div>
              <SubmitButton onClick={onClose} style={{ width: '100%' }}>
                Got it
              </SubmitButton>
            </SuccessState>
          ) : (
            <Form onSubmit={handleSubmit}>
              <p style={{ color: '#4a5568', margin: '0 0 0.5rem 0' }}>
                Fill out the form below and you'll instantly receive <strong>manager</strong> and <strong>sales rep</strong> demo credentials via email.
              </p>
              
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  placeholder="John Doe"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  placeholder="john@example.com"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                  placeholder="(555) 000-0000"
                />
              </FormGroup>

              {error && (
                <ErrorText>
                  <AlertCircle size={16} />
                  {error}
                </ErrorText>
              )}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>Processing...</>
                ) : (
                  <>Send Demo Access <Send size={18} /></>
                )}
              </SubmitButton>
            </Form>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

